/**
 * BranchSelector — First screen of tri-branch onboarding.
 * Routes operators to Cloud Account (Branch A), Gateway Hardware (Branch B), or Cold Start (Branch C).
 */

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Cloud, Radio, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';

export type OnboardingBranch = 'cloud' | 'gateway' | 'cold_start';

interface BranchSelectorProps {
  onSelect: (branch: OnboardingBranch) => void;
}

const BRANCHES: Array<{
  id: OnboardingBranch;
  title: string;
  description: string;
  detail: string;
  icon: typeof Cloud;
  enabled: boolean;
  badge?: string;
}> = [
  {
    id: 'cloud',
    title: 'Cloud Account',
    description: 'I have working portal credentials',
    detail: 'Sol-Ark, EG4, SolarEdge, FranklinWH, or other vendor portal login. We\'ll discover and import your systems automatically.',
    icon: Cloud,
    enabled: true,
  },
  {
    id: 'gateway',
    title: 'Gateway Hardware',
    description: 'System exists but cloud access is unavailable',
    detail: 'Cloud credentials are lost, expired, or the vendor API is inaccessible. Set up a provisional property and connect via gateway later.',
    icon: Radio,
    enabled: true,
    badge: 'Routes to provisional setup',
  },
  {
    id: 'cold_start',
    title: 'Not Set Up Yet',
    description: 'New install or no monitoring',
    detail: 'Equipment is on-site but has never been monitored. Create a property record, enter equipment details, and connect a gateway when ready.',
    icon: Wrench,
    enabled: true,
  },
];

export function BranchSelector({ onSelect }: BranchSelectorProps) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold text-wt-text-primary">
          How will this property connect to Watt Tower?
        </h2>
        <p className="text-sm text-wt-text-muted">
          Choose the path that matches this property's current monitoring setup.
        </p>
      </div>

      <div className="grid gap-4">
        {BRANCHES.map((branch) => {
          const Icon = branch.icon;
          return (
            <Card
              key={branch.id}
              className={cn(
                'cursor-pointer transition-colors border-wt-border',
                branch.enabled
                  ? 'hover:border-wt-primary/50 hover:bg-wt-surface-hover'
                  : 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => branch.enabled && onSelect(branch.id)}
            >
              <CardContent className="flex items-start gap-4 p-4">
                <div className="flex-shrink-0 mt-0.5 p-2 rounded-md bg-wt-surface-secondary">
                  <Icon className="h-5 w-5 text-wt-text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-wt-text-primary">{branch.title}</h3>
                    {branch.badge && (
                      <Badge variant="outline" className="text-xs">
                        {branch.badge}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-wt-text-secondary mt-0.5">{branch.description}</p>
                  <p className="text-xs text-wt-text-muted mt-1">{branch.detail}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
